/**
 * Platform brand looks, keyed by the backend's `brand_key`
 * (backend/power_backend/org/branding.py PLATFORM_BRANDS). The domain picks the
 * brand: joulelingo.com is the schools product, kilosight.com the one for
 * businesses, hospitality and landlords.
 *
 * Colours are NOT here: they arrive as the brand payload's primary_color /
 * secondary_color, so they ride the same path a partner's colours do (and a
 * partner on acme.kilosight.com can still override them). This file holds only
 * what the API does not carry: the heading typeface, the wordmark split and the
 * favicon.
 *
 * In-app wording (Houses vs Sites, pupils vs occupants) is not a brand concern
 * either: it follows the org's flavour (lib/terminology.js), whichever domain
 * the org signed up on.
 *
 * 'power' is the pre-launch default and changes nothing: no heading font
 * override, the neutral bolt mark, the default favicon.
 */
export const DEFAULT_BRAND_KEY = 'power'

export const BRAND_LOOKS = {
    power: {
        headingFont: null,
        // Whether components/BrandMark.jsx draws a mark for this brand;
        // without one, BrandLogo keeps the neutral bolt.
        hasMark: false,
        wordmark: null,
        favicon: null,
        // Marketing pages get rounder shapes, sparks and a highlighted headline word
        // (pages/marketing/usePlayful.js). The schools brand only.
        playful: false,
        // Marketing pages take the calm, data-led look built around an example site's
        // day (pages/marketing/useInsightLook.js). The business brand only.
        insight: false
    },
    joulelingo: {
        headingFont:
            "'Bricolage Grotesque Variable', 'Plus Jakarta Sans', sans-serif",
        hasMark: true,
        // Split so the name reads as joule + lingo: the first part heavy in
        // the text colour, the second lighter in the brand colour.
        wordmark: ['joule', 'lingo'],
        favicon: 'brands/joulelingo/favicon.svg',
        playful: true,
        insight: false
    },
    kilosight: {
        headingFont: "'Manrope Variable', 'Plus Jakarta Sans', sans-serif",
        hasMark: true,
        wordmark: ['kilo', 'sight'],
        favicon: 'brands/kilosight/favicon.svg',
        playful: false,
        insight: true
    }
}

/** The look for a brand key; anything unknown gets the neutral default. */
export const brandLook = (brandKey) =>
    BRAND_LOOKS[brandKey] || BRAND_LOOKS[DEFAULT_BRAND_KEY]

/**
 * The favicon a host should show, or null to leave the page's own. Partner
 * hosts never show a platform brand's mark: the partner is the face of the
 * product there, and the platform's logo would leak into their branding.
 */
export const brandFavicon = (brand) => {
    if (!brand || brand.is_partner) return null
    const file = brandLook(brand.brand_key).favicon
    // BASE_URL is '/static/frontend/' in a build and '/' in dev; absent under node:test.
    const base = import.meta.env?.BASE_URL ?? '/'
    return file ? `${base}${file}` : null
}
